/**
 * Main layout
 * @author Carlos Romera <cromera@adasasistemas.com>
 * @author Liwei Yeh <liwei.yeh@adasasistemas.com>
 */

// Library
import { RefObject, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import {
	AddressModal,
	ClientItem,
	ClientsList,
	ContentLayout,
	DeviceFaultyFormModal,
	EmailAddressModal,
	Footer,
	Header,
	ReadingModal,
	SideBar,
	SideBarItemsList,
	Skeleton,
	TermsAndConditions,
} from '@components';
// Constant
import { CONSTANT } from '@const';
// Context
import { AccountContext, LegalEntityContext, ScreenSizeContext, UserTypeContext } from '@context';
// Asset
import { Arrow } from '@public';
import { msalService } from '@services';
import developmentBarItems from '@specific/sideBarList/sideBarLists/developmentBarItems.json';
import validNonRegularUserSideBarListItems from '@specific/sideBarList/sideBarLists/validNonRegularUserSideBarListItems.json';
import validSideBarListItemMobile from '@specific/sideBarList/sideBarLists/validSideBarListItemMobile.json';
import validSideBarItems from '@specific/sideBarList/sideBarLists/validSideBarListItems.json';
import { LayoutProps, SideBarItemType, WaterIQUserType } from '@types';

// Styles
import styles from './mainLayout.module.scss';

export const CONSTANT_OTHER_ENTITIES_LITERAL = 'Other clients';
const CONSTANT_SELECT_ENTITY = 'Select client';

export const MainLayout: LayoutProps = ({ children }) => {
	const currentPath = usePathname();
	const isHomePage = currentPath === '/home';
	const { isSmallDT } = useContext(ScreenSizeContext);
	const { legalEntity, legalEntityList, filteredLegalEntityList } = useContext(LegalEntityContext);
	const { selectedUserType } = useContext(UserTypeContext);
	const { getSelectedAccount } = useContext(AccountContext);
	const [isEntityBarOpened, setIsEntityBarOpened] = useState(false);
	const sideBarRef = useRef<HTMLDivElement>(null);
	const hasEntities = legalEntityList.length > 0;

	// Memo variables
	const isValidWEHUser = useMemo(
		() => msalService.isValidAccount() && selectedUserType == WaterIQUserType.WEH,
		[selectedUserType],
	);
	const isDQPUpliftTransition = useMemo(
		() =>
			msalService.isValidAccount() &&
			selectedUserType == WaterIQUserType.DQP &&
			!CONSTANT.SHOW_DEV_PAGES,
		[selectedUserType],
	);
	const menuOptions: SideBarItemType[] = useMemo(() => {
		function getUserMenuOptions() {
			if (!msalService.isValidAccount()) {
				return [];
			} else if (selectedUserType == WaterIQUserType.DQP) {
				return validNonRegularUserSideBarListItems;
			} else {
				return getRegularUserMenuOptions();
			}
		}
		function getRegularUserMenuOptions() {
			return isSmallDT ? validSideBarListItemMobile : validSideBarItems;
		}
		return getUserMenuOptions();
	}, [isSmallDT, selectedUserType]);

	const shouldShowSidebar = useMemo(() => {
		return !isSmallDT && currentPath !== '/home';
	}, [isSmallDT, currentPath]);

	// Helpers

	/**
	 * 	Function to scroll side bar reference to the top
	 *  in case the user scrolled down the side bar when multiple legal entities
	 */
	const scrollToRef = (myRef: RefObject<HTMLDivElement | null>) =>
		setTimeout(() => {
			myRef?.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
		}, 700);

	/**
	 * 	Function to scroll side bar reference to the top
	 *  through the scrollToRef function
	 */
	const executeScroll = () => scrollToRef(sideBarRef);

	const toggleDrawer = () => {
		setIsEntityBarOpened(opened => !opened);
	};

	// Life Cycle
	/**
	 * Add access to development pages
	 */
	useEffect(() => {
		if (CONSTANT.SHOW_DEV_PAGES) {
			developmentBarItems.forEach(item => {
				if (!menuOptions.find(option => option.name == item.name)) {
					menuOptions.push(item);
				}
			});
		}
	}, [menuOptions]);

	// UI
	/**
	 * 	Fixed side bar content wrapping up the client name,
	 *  the legal entity selected, the menu items and Log out and
	 *  Terms and conditions components
	 */
	const fixedSideBar = () => {
		return (
			<>
				<SideBar customizeClassName="sideBar">
					{msalService.isValidAccount() &&
						(Boolean(getSelectedAccount?.isLoading) ? (
							<div className={styles.user}>
								<Skeleton customStyles={styles['skeleton-userName']} />
							</div>
						) : (
							<div className={styles.user} data-testid="welcomeUser">
								{getSelectedAccount?.data &&
									`${CONSTANT.WELCOME_BACK} ${getSelectedAccount.data.firstName}`}
							</div>
						))}
					{hasEntities && isValidWEHUser ? (
						<>
							<ClientItem clientItem={legalEntity} showAddress={false} testId="selectedClient" />
							<div className={styles.selectEntitiesButton}>
								{legalEntityList && legalEntityList.length > 1 && (
									<span className={styles.selectEntitiesTitleButton}>
										<span onClick={toggleDrawer} data-testid="otherEntities">
											{CONSTANT_OTHER_ENTITIES_LITERAL}
										</span>
										<button
											className={styles.arrow}
											aria-label={CONSTANT_OTHER_ENTITIES_LITERAL}
											tabIndex={0}
											onClick={toggleDrawer}
										>
											<Arrow customStyles={styles.arrow} />
										</button>
									</span>
								)}
							</div>
						</>
					) : isValidWEHUser ? (
						<>
							<ClientItem clientItem={legalEntity} showAddress={false} testId="selectedClient" />
							<div className={styles.selectEntitiesButton} />
						</>
					) : (
						<></>
					)}

					<nav
						className={styles.sideBarItemsContainer}
						role="menubar"
						aria-label="Main menu"
						onClick={() => {
							setIsEntityBarOpened(false);
							executeScroll();
						}}
					>
						<SideBarItemsList options={menuOptions} fade={isEntityBarOpened} />
					</nav>
				</SideBar>
			</>
		);
	};

	/**
	 * 	Hidden side bar content wrapping up the legal entities component,
	 *  except the one it is selected in the fixed side bar component
	 */
	const hiddenSideBar = () => {
		return (
			<SideBar customizeClassName="hiddenSideBar" isOpened={isEntityBarOpened}>
				<div className={styles.closeWraperButton} onClick={toggleDrawer}>
					<button className={styles.backArrow} aria-label="Close entity selection">
						<Arrow customStyles={styles.backArrow} />
					</button>
					<div>{CONSTANT_SELECT_ENTITY}</div>
				</div>
				<div ref={sideBarRef} className={styles.subWrapperClientList}>
					<ClientsList
						clients={filteredLegalEntityList}
						onSelect={() => {
							setIsEntityBarOpened(false);
							executeScroll();
						}}
					/>
				</div>
			</SideBar>
		);
	};

	/**
	 * Mobile navigation bar that appears at the bottom of the page
	 * on small screens, providing access to menu items
	 */
	const mobileBottomNavBar = () => {
		return (
			<SideBar customizeClassName="sideBar">
				<nav
					className={styles.sideBarItemsContainer}
					role="menubar"
					aria-label="Mobile navigation menu"
					onClick={() => {
						setIsEntityBarOpened(false);
						executeScroll();
					}}
				>
					<SideBarItemsList options={menuOptions} fade={isEntityBarOpened} />
				</nav>
			</SideBar>
		);
	};

	return (
		<div className={styles.wrapper}>
			{!isDQPUpliftTransition && <Header />}
			<div className={styles.content}>
				<TermsAndConditions />
				<div
					onClick={() => {
						setIsEntityBarOpened(false);
						executeScroll();
					}}
					className={[styles.backdrop, isEntityBarOpened ? styles['backdrop-show'] : ''].join(' ')}
				/>
				{!isDQPUpliftTransition && shouldShowSidebar && (
					<div className={styles.sideBarWrapper}>
						{fixedSideBar()}
						{hiddenSideBar()}
					</div>
				)}
				{isHomePage ? (
					<div className={styles.homeContent}>{children}</div>
				) : (
					<ContentLayout>{children}</ContentLayout>
				)}
				<ReadingModal />
				<DeviceFaultyFormModal />
				<AddressModal />
				<EmailAddressModal />
			</div>
			{!isDQPUpliftTransition && <Footer />}
			{isSmallDT && isValidWEHUser && mobileBottomNavBar()}
		</div>
	);
};

export default MainLayout;
